import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Crown, TrendingUp, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AccountSettings from "@/components/AccountSettings";

export default function Nav() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const isInnerCircle = user && (user.tier === "lifetime" || user.founding_member);

  const closeMenu = () => setMenuOpen(false);
  const doLogout = async () => { setMenuOpen(false); await logout(); nav("/"); };

  const mobileLink = "py-3 text-base text-stone-700 border-b border-stone-200/60";

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50"
      style={{ background: "rgba(253,251,247,0.55)", backdropFilter: "blur(14px)", borderBottom: "1px solid rgba(28,25,23,0.06)" }}
      data-testid="app-nav"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2" data-testid="nav-home-link" onClick={closeMenu}>
          <img src="/logo.jpg" alt="LoomPDF logo" className="w-8 h-8 rounded-lg object-cover" style={{ imageRendering: "crisp-edges" }} />
          <div className="font-display text-2xl">LoomPDF</div>
          <span className="font-hand text-stone-500 ml-1 hidden sm:inline">studio</span>
        </Link>

        {/* Desktop / tablet nav (>= 768px) — unchanged */}
        <nav className="hidden md:flex items-center gap-3" data-testid="desktop-nav">
          {user ? (
            <>
              <Link to="/dashboard" className="btn-ghost text-sm" data-testid="nav-dashboard-link">My decks</Link>
              <Link to="/blog" className="btn-ghost text-sm" data-testid="nav-blog-link-auth">Blog</Link>
              {isInnerCircle && (
                <Link to="/circle" className="btn-ghost text-sm inline-flex items-center gap-1.5" data-testid="nav-circle-link">
                  <Crown className="w-3.5 h-3.5 text-amber-500"/> Circle
                </Link>
              )}
              <Link to="/pricing" className="btn-ghost text-sm" data-testid="nav-pricing-link">Pricing</Link>
              {user.is_admin && (
                <Link to="/admin" className="btn-ghost text-sm inline-flex items-center gap-1.5" data-testid="nav-admin-link">
                  <TrendingUp className="w-3.5 h-3.5 text-indigo-500"/> Admin
                </Link>
              )}
              <AccountSettings />
              <div className="hidden sm:flex items-center gap-2 px-3">
                {user.picture && <img src={user.picture} className="w-7 h-7 rounded-full" alt=""/>}
                <span className="text-sm text-stone-700" data-testid="nav-user-name">{user.name}</span>
                {isInnerCircle && (
                  <span className="inline-flex items-center gap-1 text-[0.65rem] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid="inner-circle-badge">
                    <Crown className="w-3 h-3"/> Inner Circle
                  </span>
                )}
              </div>
              <button onClick={async()=>{await logout(); nav("/");}} className="btn-ghost text-sm" data-testid="nav-logout-button">Sign out</button>
            </>
          ) : (
            <>
              <Link to="/blog" className="btn-ghost text-sm" data-testid="nav-blog-link">Blog</Link>
              <Link to="/login" className="btn-ghost text-sm" data-testid="nav-login-link">Sign in</Link>
              <Link to="/" className="btn-primary text-sm" data-testid="nav-register-link">Upload free</Link>
            </>
          )}
        </nav>

        {/* Mobile hamburger (< 768px) */}
        <button
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-stone-700 hover:bg-stone-100 transition-colors"
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
          data-testid="mobile-menu-button"
        >
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile dropdown (< 768px) */}
      {menuOpen && (
        <div
          className="md:hidden px-6 pb-4 flex flex-col"
          style={{ background: "rgba(253,251,247,0.98)", backdropFilter: "blur(14px)", borderTop: "1px solid rgba(28,25,23,0.06)" }}
          data-testid="mobile-menu"
        >
          {user ? (
            <>
              <div className="flex items-center gap-2 py-3 border-b border-stone-200/60">
                {user.picture && <img src={user.picture} className="w-8 h-8 rounded-full" alt=""/>}
                <span className="text-sm text-stone-700" data-testid="mobile-user-name">{user.name}</span>
                {isInnerCircle && (
                  <span className="inline-flex items-center gap-1 text-[0.6rem] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                    <Crown className="w-3 h-3"/> Inner Circle
                  </span>
                )}
              </div>
              <Link to="/dashboard" onClick={closeMenu} className={mobileLink} data-testid="mobile-nav-dashboard-link">My decks</Link>
              <Link to="/blog" onClick={closeMenu} className={mobileLink} data-testid="mobile-nav-blog-link-auth">Blog</Link>
              {isInnerCircle && (
                <Link to="/circle" onClick={closeMenu} className={`${mobileLink} inline-flex items-center gap-1.5`} data-testid="mobile-nav-circle-link">
                  <Crown className="w-4 h-4 text-amber-500"/> Circle
                </Link>
              )}
              <Link to="/pricing" onClick={closeMenu} className={mobileLink} data-testid="mobile-nav-pricing-link">Pricing</Link>
              {user.is_admin && (
                <Link to="/admin" onClick={closeMenu} className={`${mobileLink} inline-flex items-center gap-1.5`} data-testid="mobile-nav-admin-link">
                  <TrendingUp className="w-4 h-4 text-indigo-500"/> Admin
                </Link>
              )}
              <div className="py-2 border-b border-stone-200/60" onClick={closeMenu} data-testid="mobile-nav-settings">
                <AccountSettings />
              </div>
              <button onClick={doLogout} className="py-3 text-base text-left text-stone-700" data-testid="mobile-nav-logout-button">Sign out</button>
            </>
          ) : (
            <>
              <Link to="/blog" onClick={closeMenu} className={mobileLink} data-testid="mobile-nav-blog-link">Blog</Link>
              <Link to="/login" onClick={closeMenu} className={mobileLink} data-testid="mobile-nav-login-link">Sign in</Link>
              <Link to="/" onClick={closeMenu} className="py-3 text-base text-indigo-600 font-medium" data-testid="mobile-nav-register-link">Upload free</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
